import React, { useState, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import Button from "Components/common/Button";
import { whatsAppReplyState } from "./state";
import userDetail from "Stores/userDetail";
import { startWaAuthPopupPolling } from "./waAuthPopupPolling";

const ConnectWhatsAppButton = observer(() => {
  const [loading, setLoading] = useState(false);
  const [pendingAccountId, setPendingAccountId] = useState("");
  const [showConfirmInput, setShowConfirmInput] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const popupRef = useRef(null);
  const stopWatchRef = useRef(null);

  const onPopupClosed = () => {
    whatsAppReplyState.clearWaAuthPopup();
    setPopupOpen(false);
    whatsAppReplyState.getWhatsappAccounts();
  };

  useEffect(() => {
    return () => {
      const stop = stopWatchRef.current;
      if (stop) stop();
      stopWatchRef.current = null;
    };
  }, []);

  const handleConnect = async () => {
    setLoading(true);
    // Open popup synchronously within the user gesture to avoid Chrome's popup blocker.
    // After await, the gesture context is lost and Chrome blocks window.open().
    const popup = window.open("about:blank", "_blank", "width=600,height=700");
    const url = await whatsAppReplyState.getWhatsappAuthUrl();
    setLoading(false);
    if (url && popup) {
      popup.location.href = url;
      popupRef.current = popup;
      whatsAppReplyState.registerWaAuthPopup(popup);
      setPopupOpen(true);
      const prevStop = stopWatchRef.current;
      if (prevStop) prevStop();
      stopWatchRef.current = startWaAuthPopupPolling(popup, () => {
        stopWatchRef.current = null;
        onPopupClosed();
      });
    } else {
      if (popup) popup.close();
    }
  };

  const handleConfirm = async () => {
    if (!pendingAccountId.trim()) return;
    await whatsAppReplyState.confirmWhatsappConnection(pendingAccountId.trim());
    setShowConfirmInput(false);
    setPendingAccountId("");
  };

  const connectBtnProps = {
    id: "connectWhatsAppBtn",
    name: "connectWhatsAppBtn",
    btnText: loading ? "Loading..." : "Connect WhatsApp",
    type: "button",
    className: `addLinkedInAccountBtn ${loading ? "loading" : ""}`,
    click: handleConnect,
    loader: loading
  };

  const confirmBtnProps = {
    id: "confirmWhatsAppBtn",
    name: "confirmWhatsAppBtn",
    btnText: whatsAppReplyState.connectLoading ? "Connecting..." : "Confirm",
    type: "button",
    className: `saveButton ${whatsAppReplyState.connectLoading ? "loading" : ""}`,
    click: handleConfirm,
    loader: whatsAppReplyState.connectLoading
  };

  const { noOfWaAccountsEligible = 0 } = userDetail?.userFeatureFlag || {};
  const isPlanBlocked = noOfWaAccountsEligible === 0;

  return (
    <div className="liConnectWrapper">
      <i className="material-icons waEmptyIcon" style={{ fontSize: "80px", color: "#25D366", marginBottom: "16px" }}>
        chat
      </i>
      <h6>Add your WhatsApp account</h6>
      <p style={{ color: "#888", marginBottom: "16px", fontSize: "13px", textAlign: "center", maxWidth: "360px" }}>
        Connect your WhatsApp account to send automated messages and follow-ups to your prospects.
      </p>
      {isPlanBlocked ? (
        <div style={{ textAlign: "center", maxWidth: "360px" }}>
          <p style={{ color: "#c00", fontSize: "13px", marginBottom: "12px" }}>
            WhatsApp accounts are not available on your current plan. Please upgrade to connect a WhatsApp account.
          </p>
        </div>
      ) : !popupOpen && !showConfirmInput ? (
        <div className="liButtonWrapper">
          <Button {...connectBtnProps} />
        </div>
      ) : popupOpen && !showConfirmInput ? (
        <div className="waPopupWaiting" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", width: "100%", maxWidth: "360px" }}>
          <p style={{ fontSize: "13px", color: "#555", textAlign: "center" }}>
            A window has opened. Scan the QR code there to connect your WhatsApp account. When you see the success message, click Close in that window—this page will update automatically.
          </p>
          <Button
            id="cancelWaConnect"
            name="cancelWaConnect"
            btnText="I've closed the popup"
            type="button"
            className="cancelButton"
            click={() => {
              const stop = stopWatchRef.current;
              if (stop) stop();
              stopWatchRef.current = null;
              try {
                const p = popupRef.current;
                if (p) p.close();
              } catch (e) {
                // ignore
              }
              onPopupClosed();
            }}
          />
          <button
            type="button"
            onClick={() => { setShowConfirmInput(true); setPopupOpen(false); }}
            style={{ background: "none", border: "none", color: "#666", fontSize: "12px", cursor: "pointer", textDecoration: "underline" }}
          >
            Connection didn&apos;t complete? Enter Account ID manually
          </button>
        </div>
      ) : (
        <div className="waConfirmWrapper" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", width: "100%", maxWidth: "360px" }}>
          <p style={{ fontSize: "13px", color: "#555", textAlign: "center" }}>
            Enter the Account ID from the success page (only if the connection did not complete automatically).
          </p>
          <input
            type="text"
            placeholder="Enter Account ID from success page"
            value={pendingAccountId}
            onChange={e => setPendingAccountId(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "13px"
            }}
          />
          <div style={{ display: "flex", gap: "8px" }}>
            <Button {...confirmBtnProps} />
            <Button
              id="cancelWaConnect"
              name="cancelWaConnect"
              btnText="Cancel"
              type="button"
              className="cancelButton"
              click={() => {
                setShowConfirmInput(false);
                setPendingAccountId("");
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
});

export { ConnectWhatsAppButton };
export default ConnectWhatsAppButton;
